import { motion } from 'framer-motion';
import { Crown, Sparkles } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useSubscription } from '@/hooks/useSubscription';
import { openPaymentUrl } from '@/lib/desktopBridge';
import { toast } from 'sonner';

interface UpgradePromptProps {
  feature: string;
  description?: string;
  compact?: boolean;
}

export function UpgradePrompt({ feature, description, compact = false }: UpgradePromptProps) {
  const { upgradeToPro, tier } = useSubscription();

  const handleUpgrade = async () => {
    const result = await upgradeToPro();
    if (result.checkoutUrl) {
      openPaymentUrl(result.checkoutUrl);
      return;
    }
    if (result.error) {
      toast.error(result.error);
      return;
    }
    // e.g. iOS IAP success without URL
    if (result.success) toast.success('Upgrade complete');
  };

  if (tier === 'pro' || tier === 'free_for_life') {
    return null;
  }

  if (compact) {
    return (
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        className="flex items-center gap-2 p-3 rounded-lg bg-primary/10 border border-primary/20"
      >
        <Crown className="h-4 w-4 text-primary" />
        <span className="text-sm text-muted-foreground flex-1">
          Upgrade to unlock {feature}
        </span>
        <Button size="sm" variant="ghost" onClick={handleUpgrade} className="text-primary">
          Upgrade
        </Button>
      </motion.div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      className="relative overflow-hidden rounded-xl border border-primary/20 bg-gradient-to-br from-primary/5 to-primary/10 p-6"
    >
      {/* Glow effect */}
      <div className="absolute -top-20 -right-20 w-40 h-40 bg-primary/20 rounded-full blur-3xl" />
      
      <div className="relative z-10 flex flex-col items-center text-center gap-4">
        <div className="w-12 h-12 rounded-full bg-primary/20 flex items-center justify-center">
          <Sparkles className="h-6 w-6 text-primary" />
        </div>
        
        <div>
          <h3 className="text-lg font-semibold mb-1">Unlock {feature}</h3>
          <p className="text-sm text-muted-foreground">
            {description || 'This feature is available with Luma Pro.'}
          </p>
        </div>
        
        <Button onClick={handleUpgrade} className="gap-2">
          <Crown className="h-4 w-4" />
          Upgrade to Pro
        </Button>
        
        <p className="text-xs text-muted-foreground">
          $4.99/month • Cancel anytime
        </p>
      </div>
    </motion.div>
  );
}
